import { replaceAql, appendAql, removeAql } from '../utils/aqlStringUtils'
import { mapAllFiltersToAQLFields } from '../utils/mapFiltersToAQLFields'
import { targetedAlphanumericFieldPattern } from '../../regexp'
import type { AlphaNumericFilterKey, SelectValueFilterKey } from '../utils/mapFiltersToAQLFields'
import type { AllDashboardCardFilters, SearchOperatorConfig } from '../../types'
import { regexExecAll } from '../utils/regexExecAll'
import { regexpCaptureGroupsToSearchOperatorConfig } from '../utils/searchOperatorConfig'

// "make" | "model" | "color" | "lane" | "run" | "seller" | "buyer" | "creatorFullName" | "followupFullName" | "completedFullName"
// "isFavorite" | "contactMethod" | "contactStatus" | "saleStatus" | "hasCr" | "hasTitle"
export const parseAlphaNumericField = (
    aql: string,
    searchKey: AlphaNumericFilterKey | SelectValueFilterKey,
): Partial<AllDashboardCardFilters> => {
    const emptyConfig = { [searchKey]: '' }

    if (!aql) {
        return emptyConfig
    }

    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []

    if (!aqlFields.length) {
        return {}
    }

    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const allMatches = (aqlFields as unknown as string[]).reduce<RegExpExecArray[]>((acc, field) => {
        if (acc.length) {
            return acc
        }

        const pattern = targetedAlphanumericFieldPattern(field)
        const regex = new RegExp(pattern, 'gi')
        const maybeMatches = regexExecAll(aql, regex)

        return maybeMatches?.length ? maybeMatches : []
    }, [])

    if (!allMatches.length) {
        return emptyConfig
    }

    const firstConfig = allMatches.reduce<SearchOperatorConfig | null>((acc, match) => {
        if (acc) {
            return acc
        }

        const config = regexpCaptureGroupsToSearchOperatorConfig(match.groups)

        if (!config.operator || !config.lowValue) {
            return null
        }

        return config
    }, null)

    if (!firstConfig) {
        return emptyConfig
    }

    const valueWithOperator = firstConfig.lowValue

    return { [searchKey]: valueWithOperator }
}

export const updateAlphaNumericField = (
    prior: string,
    searchKey: AlphaNumericFilterKey | SelectValueFilterKey,
    value = '',
): string => {
    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []
    const primaryAqlField = aqlFields[0]

    if (!primaryAqlField) {
        return prior
    }

    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const preExistingAQLField = (aqlFields as unknown as string[]).reduce((acc, searchField) => {
        if (acc) {
            return acc
        }

        const pattern = targetedAlphanumericFieldPattern(searchField)
        const regex = new RegExp(pattern, 'i')
        const match = regex.exec(prior)
        return match?.groups?.key || ''
    }, '')

    if (!value) {
        return preExistingAQLField ? removeAql(prior, preExistingAQLField) : prior
    }

    return preExistingAQLField
        ? replaceAql(prior, preExistingAQLField, preExistingAQLField, value)
        : appendAql(prior, primaryAqlField, value)
}
